import { useCallback, useRef, useState } from 'react';

import { deleteMeeting } from '../services/meetingDelete';

/**
 * The confirm-then-delete interaction shared by every screen that can
 * permanently delete a meeting (the Meetings list today; the meeting
 * details page can reuse it the same way) - mirrors hooks/
 * useEndMeeting.js exactly. request an end (opens the confirmation),
 * cancel it, or confirm it; the actual delete - and its
 * backend-is-the-source-of-truth handling of an already-gone meeting -
 * lives in services/meetingDelete.js.
 *
 * Usage: render <DeleteMeetingModal> while `deleteTarget` is set.
 *
 * `onSettled({ deleted, target, code })` runs once the attempt
 * finished, so the caller can drop the row from its own list. Nothing
 * is assumed deleted by this hook itself.
 */
export function useDeleteMeeting({ onSettled } = {}) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // A ref as well as state: two quick confirms must never send two deletes.
  const inFlightRef = useRef(false);

  const requestDelete = useCallback((target) => {
    setDeleteError('');
    setDeleteTarget(target);
  }, []);

  const cancelDelete = useCallback(() => {
    if (inFlightRef.current) return;
    setDeleteTarget(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || inFlightRef.current) return;

    inFlightRef.current = true;
    setIsDeleting(true);
    setDeleteError('');

    const target = deleteTarget;
    const result = await deleteMeeting(target.id);

    inFlightRef.current = false;
    setIsDeleting(false);

    if (result.deleted) {
      setDeleteTarget(null);
    } else {
      // Left open so the user can see the error next to the still-open
      // confirmation and retry, rather than being bounced back to a
      // silently-unchanged row.
      setDeleteError(result.errorMessage);
    }

    if (onSettled) onSettled({ ...result, target });
  }, [deleteTarget, onSettled]);

  return {
    deleteTarget,
    // The id whose delete request is in flight right now, for disabling
    // that meeting's own Delete action.
    deletingId: isDeleting && deleteTarget ? deleteTarget.id : null,
    isDeleting,
    deleteError,
    requestDelete,
    cancelDelete,
    confirmDelete,
  };
}
